import { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { HiUser, HiLocationMarker, HiPhone, HiLockClosed, HiCheck, HiExclamation } from 'react-icons/hi';

export default function Profile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ name: '', address: '', phone: '' });
  const [message, setMessage] = useState({ text: '', type: '' }); // success/error
  const [loading, setLoading] = useState(false);

  // Password change state
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwMsg, setPwMsg] = useState({ text: '', type: '' });
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    api.get('/api/user/profile')
      .then(res => setProfile(res.data))
      .catch(() => {});
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });
    setLoading(true);
    try {
      await api.put('/api/user/profile', profile);
      setMessage({ text: 'Profile updated successfully!', type: 'success' });
    } catch {
      setMessage({ text: 'Update failed. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    setPwMsg({ text: '', type: '' });
    setPwLoading(true);
    try {
      await api.put('/api/user/change-password', { oldPassword: oldPw, newPassword: newPw });
      setPwMsg({ text: 'Password changed successfully!', type: 'success' });
      setOldPw('');
      setNewPw('');
    } catch {
      setPwMsg({ text: 'Password change failed. Check your current password.', type: 'error' });
    } finally {
      setPwLoading(false);
    }
  };

  // Shared input class (same style as checkout)
  const inputClass = "w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-[15px] placeholder:text-gray-400 focus:bg-white focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 transition outline-none";
  const labelClass = "block text-[13px] font-medium text-gray-700 mb-1.5";

  return (
    <div className="max-w-2xl mx-auto px-4 py-10 sm:py-12">
      <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-8">My Profile</h2>

      {/* Profile update card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-8 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Personal Information</h3>
        <form onSubmit={handleUpdate} className="space-y-5">
          <div>
            <label className={labelClass}>Full Name</label>
            <div className="relative">
              <HiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="John Doe"
                className={inputClass}
                value={profile.name}
                onChange={e => setProfile({ ...profile, name: e.target.value })}
                required
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Address</label>
            <div className="relative">
              <HiLocationMarker className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="123 Main Street, City"
                className={inputClass}
                value={profile.address}
                onChange={e => setProfile({ ...profile, address: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Phone Number</label>
            <div className="relative">
              <HiPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                placeholder="+234 801 234 5678"
                className={inputClass}
                value={profile.phone}
                onChange={e => setProfile({ ...profile, phone: e.target.value })}
              />
            </div>
          </div>

          {message.text && (
            <div className={`flex items-center gap-2 text-sm p-3 rounded-lg ${
              message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
            }`}>
              {message.type === 'success' ? <HiCheck size={18} /> : <HiExclamation size={18} />}
              {message.text}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-black transition disabled:opacity-60"
          >
            {loading ? 'Updating…' : 'Update Profile'}
          </button>
        </form>
      </div>

      {/* Password change card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Change Password</h3>
        <form onSubmit={handlePassword} className="space-y-5">
          <div>
            <label className={labelClass}>Current Password</label>
            <div className="relative">
              <HiLockClosed className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                placeholder="••••••••"
                className={inputClass}
                value={oldPw}
                onChange={e => setOldPw(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>New Password</label>
            <div className="relative">
              <HiLockClosed className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="password"
                placeholder="••••••••"
                className={inputClass}
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                required
              />
            </div>
          </div>

          {pwMsg.text && (
            <div className={`flex items-center gap-2 text-sm p-3 rounded-lg ${
              pwMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
            }`}>
              {pwMsg.type === 'success' ? <HiCheck size={18} /> : <HiExclamation size={18} />}
              {pwMsg.text}
            </div>
          )}

          <button
            type="submit"
            disabled={pwLoading}
            className="w-full bg-gray-800 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-gray-900 transition disabled:opacity-60"
          >
            {pwLoading ? 'Changing…' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}